import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import { ImportEmployeesModalPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import legalEntities from 'modules/core/store/collections/legalEntities'

import DocumentUploader from 'modules/documents/components/Documents/DocumentUploader'
import LegalEntityButton from 'modules/employees/components/LegalEntityButton'
import LegalEntitySelect from 'modules/employees/components/LegalEntitySelect'
import OverlimitEmployeesFootNote from 'modules/employees/components/OverlimitEmployees/FootNote'
import { TipLink, Tips } from 'modules/employees/components/Tips'
import useAppendErrors from 'modules/employees/hooks/useAppendErrors'
import useLegalEntityOptions from 'modules/employees/hooks/useLegalEntityOptions'
import useProcessFile from 'modules/employees/hooks/useProcessFile'
import type { EmployeeImporterContextProps } from 'modules/employees/lib/employeesImporterContext'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

const DOWNLOAD_PATH = '/onboarding_importer/imports'
const CHECK_DOWNLOAD = '/onboarding_importer/download_available'

const TipsComponent = () => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const tips = React.useRef([
    i18n.tView('import.tips.before_importing', {
      link: (
        <TipLink
          text={i18n.t('import.tips.configure_timeoff')}
          onClick={() => {
            navigate(navigation.settings.timeoffPolicies.fullPath())
          }}
        />
      ),
    }),
    i18n.tView('import.tips.import_or_update_how_to', {
      link: (
        <TipLink
          text={i18n.t('import.tips.how_to_import')}
          href={helpCenterUrl('import_employees')}
          target="_blank"
        />
      ),
    }),
    i18n.t('import.tips.large_file'),
  ]).current

  return <Tips tips={tips} />
}

interface Props {
  importer: EmployeeImporterContextProps
  onClose: () => void
}

class EmployeesFileForm extends FactorialForm {
  constructor(values: { file: File | undefined | null }) {
    super(values, {
      file: 'file',
    })
  }
}

const ImportEmployeesModalPage = ({ importer, onClose }: Props) => {
  const api = React.useRef(ApiClient()).current
  const options = useLegalEntityOptions(legalEntities).get()
  const initialFile = importer.files.length > 0 ? importer.files[0] : null
  const form = React.useRef(new EmployeesFileForm({ file: initialFile })).current
  const fileField = form.get('file')
  const [upgradeModalOpen, setUpgradeModalOpen] = React.useState(false)
  const { data: importerData, loading } = useQuery(ImportEmployeesModalPageDocument)
  const canImportEmployees =
    importerData?.onboardingImporter?._policies.importEmployees.can || false

  const { running, onSubmit, error, value } = useProcessFile({
    api: api,
    file: fileField.value,
    importPath: DOWNLOAD_PATH,
  })
  const {
    company: { id: companyId, subscription },
    access,
  } = useCurrentSession()

  useAppendErrors(error, fileField, value)

  const onDropFile = (files: File[]) => {
    form.cleanAll()
    fileField.setErrors(null)
    fileField.set(files[0])
  }

  const buttonText = i18n.t('import.download_template')

  useEffect(() => {
    track(trackingEvents.oldImportEmployees.startOldEmployeesImporter, {
      companyId: companyId,
      acessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (running) track(trackingEvents.employee.inBulk.importFileSubmit)
  }, [running])

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <AuthorizeUser authorizeFn={() => canImportEmployees}>
        <Modal
          illustration="group"
          title={i18n.t('employees.import_title')}
          description={i18n.t('import.description')}
          onClose={onClose}
          onSubmit={onSubmit}
          footer={
            <Button disabled={!fileField.value || running} submit>
              {i18n.t('import.process_file')}
            </Button>
          }
        >
          {loading && <Spinner />}
          {!loading && (
            <Box gap="s24">
              <Box flexDirection="column" justifyContent="center" alignItems="center" gap="s16">
                {legalEntities.length < 5 ? (
                  legalEntities.map((legalEntity) => (
                    <LegalEntityButton
                      key={legalEntity.id}
                      legalEntity={legalEntity}
                      checkDownload={CHECK_DOWNLOAD}
                      downloadPath={DOWNLOAD_PATH}
                    />
                  ))
                ) : (
                  <LegalEntitySelect
                    options={options}
                    buttonText={buttonText}
                    checkDownload={CHECK_DOWNLOAD}
                    downloadPath={DOWNLOAD_PATH}
                  />
                )}
              </Box>
              <DocumentUploader
                field={fileField}
                placeholder={i18n.t('employees.import_placeholder')}
                onDropped={onDropFile}
              />
              <TipsComponent />
              <OverlimitEmployeesFootNote setUpgradeModalOpen={setUpgradeModalOpen} />
            </Box>
          )}
        </Modal>
        {upgradeModalOpen && (
          <RequestUpgradeModal
            requestReason={ValidReasons.UpgradeFromLegacy}
            requestSource="employees-importer"
            onClose={() => onClose}
          />
        )}
      </AuthorizeUser>
    </ExpiredTrialChecker>
  )
}

export default observer(ImportEmployeesModalPage)
